import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import scrollDatePlugin from "flatpickr/dist/plugins/scrollPlugin";
import { Plugin } from "flatpickr/dist/types/options";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import Flatpickr from "react-flatpickr";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../../store/store";

import styles from "./index.module.scss";
import "flatpickr/dist/themes/light.css";
import "flatpickr/dist/plugins/monthSelect/style.css";
import "./index.scss";

interface IDatePickerProps {
  defaultDate?: string[];
  valueKey?: string;
  displayFormat?: string;
  returnFormat?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  mode?: "time" | "multiple" | "range" | "single";
  select?: "month" | "day";
  label?: string;
  errorMessage?: string;
  placeholder: string;
  name?: string;
  isRequired?: boolean;
  className?: string;
  onChange?: ({
    value,
    valueKey,
  }: {
    value: string[];
    valueKey?: string;
  }) => void;
}

export interface DatePickerHandler {
  handleResetDate: () => void;
}

// Plugins for month selection and date scrolling
const monthSelect: Plugin = new (monthSelectPlugin as any)({
  dateFormat: "F Y",
  theme: "light",
});

const scrollDate: Plugin = new (scrollDatePlugin as any)({
  dateFormat: "mMM-Y",
  theme: "light",
});

const datePluginsLinking = {
  month: monthSelect,
  day: scrollDate,
};

const DatePicker = forwardRef<DatePickerHandler, IDatePickerProps>(
  (
    {
      displayFormat,
      minDate,
      maxDate,
      defaultDate = [],
      valueKey = "",
      returnFormat = "",
      mode = "range",
      select = "month",
      label = "",
      errorMessage = "",
      placeholder = "",
      name = "",
      isRequired = false,
      className = "",
      onChange = () => {},
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const { date_format } = useTypedSelector((state) => state.environment);

    const id = useMemo(nanoid, []);
    const flatpickrRef = useRef<Flatpickr>(null);

    const pickerPlugin = useMemo(
      () => datePluginsLinking[select ?? "month"],
      [],
    );

    const isDefaultDateProvided = useMemo(
      () => defaultDate && defaultDate?.filter((el) => el).length > 0,
      [defaultDate],
    );

    const handleDateChange = (newDate: Date[]) => {
      const value = newDate.map((date) => {
        const formattedNow = dayjs().format(returnFormat);
        const formattedDate = dayjs(
          formattedNow,
          { format: returnFormat },
          true,
        );
        const isDateValid = formattedDate.isValid();

        if (isDateValid) {
          return dayjs(date).format(returnFormat);
        } else {
          return dayjs(date).format(date_format || "MM/DD/YYYY");
        }
      });

      if (onChange && (mode !== "range" || value.length > 0)) {
        if (
          value.length === 2 &&
          value[0] === value[1] &&
          valueKey === "date"
        ) {
          value[1] = "";
        }

        onChange({ value, valueKey });
      }
    };

    useImperativeHandle(ref, () => ({
      handleResetDate: () => flatpickrRef.current?.flatpickr.clear(),
    }));

    return (
      <div className={classNames(styles.container, className)}>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
            {isRequired && <span className={styles.asterisk}>*</span>}
          </label>
        )}

        <Flatpickr
          ref={flatpickrRef}
          name={name}
          value={
            isDefaultDateProvided
              ? defaultDate?.map((el) => dayjs(el).toDate())
              : defaultDate
          }
          className={classNames(styles.picker, {
            [styles.picker_error]: !!errorMessage,
          })}
          type="text"
          options={{
            mode,
            minDate: minDate ? minDate.toDate() : undefined,
            maxDate: maxDate ? maxDate.toDate() : undefined,
            plugins: [pickerPlugin],
            dateFormat: displayFormat ?? "MM-YYYY",
            defaultDate: isDefaultDateProvided
              ? defaultDate?.map((el) => dayjs(el).toDate())
              : defaultDate,
          }}
          placeholder={`${placeholder || t("Select date")}${isRequired && "*"}`}
          onChange={handleDateChange}
          onClose={handleDateChange}
        />

        <span className={styles.error}>{errorMessage}</span>
      </div>
    );
  },
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
